/*
 * nf-sticky-atc.js — barra de compra fixa no mobile (Fase 4.3) + atualizacao
 * ao vivo do parcelamento (Fase 4.4).
 * -------------------------------------------------------------------------
 * Custom element <nf-sticky-atc>:
 *  - IntersectionObserver no botao de compra principal: mostra a barra quando ele
 *    sai da viewport, esconde quando volta;
 *  - o botao "Adicionar" dispara requestSubmit() no form principal (caminho unico
 *    de submit — reusa o product-form.js, sem duplicar logica);
 *  - na troca de variante (evento variant-change do Dawn) atualiza preco, imagem,
 *    estado do botao e a linha de parcelamento (nf-installments);
 *  - esconde quando o cart-drawer abre (compat com a F5).
 *
 * Sem dependencia de internals: usa a interface publica (subscribe / PUB_SUB_EVENTS /
 * requestSubmit / window.variantStrings), toda ela ja presente no Dawn.
 */
/*
 * Conta do parcelamento/Pix em centavos inteiros — espelho exato do Liquid de
 * snippets/nf-installments.liquid (divided_by e times truncam). Exposta em window
 * para o teste (tests/nf-installments-pix.test.cjs) e para reuso.
 */
window.nfInstallmentsMath = {
  compute: function (priceCents, opts) {
    opts = opts || {};
    var max = Math.max(1, parseInt(opts.max, 10) || 1);
    var minCents = parseInt(opts.minCents, 10) || 0;
    var pixPct = parseInt(opts.pixPct, 10) || 0;
    var price = Math.max(0, Math.floor(Number(priceCents) || 0));
    if (price <= 0) return { count: 0, perCents: 0, pixCents: null };
    var count = max;
    if (minCents > 0) count = Math.min(max, Math.floor(price / minCents));
    if (count < 1) count = 1;
    var per = Math.floor(price / count);
    var pix = pixPct > 0 ? Math.floor((price * (100 - pixPct)) / 100) : null;
    return { count: count, perCents: per, pixCents: pix };
  },
};

if (!customElements.get('nf-sticky-atc')) {
  customElements.define(
    'nf-sticky-atc',
    class NfStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.formId;

        this.addButton = this.querySelector('[data-nf-sticky-add]');
        this.addLabel = this.querySelector('[data-nf-sticky-add-label]');
        this.priceEl = this.querySelector('[data-nf-sticky-price]');
        this.img = this.querySelector('[data-nf-sticky-img]');

        this.mainForm = document.getElementById(this.formId);
        this.mainSubmit = document.getElementById('ProductSubmitButton-' + this.sectionId);
        this.productInfo = this.closest('product-info') || document.getElementById('MainProduct-' + this.sectionId);
        this.installments = this.productInfo && this.productInfo.querySelector('[data-nf-installments]');
        this.drawer = document.querySelector('cart-drawer');

        // Caminho unico de submit.
        this._onAdd = () => {
          if (!this.mainForm) return;
          if (this.mainSubmit && this.mainSubmit.hasAttribute('disabled')) return;
          if (typeof this.mainForm.requestSubmit === 'function') {
            this.mainForm.requestSubmit();
          } else {
            this.mainForm.submit();
          }
        };
        if (this.addButton) this.addButton.addEventListener('click', this._onAdd);

        // Mostrar/ocultar conforme o botao principal entra/sai da viewport.
        if (this.mainSubmit && 'IntersectionObserver' in window) {
          this._io = new IntersectionObserver(
            (entries) => this.toggle(!entries[0].isIntersecting),
            { root: null, threshold: 0 }
          );
          this._io.observe(this.mainSubmit);
        }

        // Atualizacao na troca de variante.
        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
          this._unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
        }

        // Ocultar quando o cart-drawer abrir (compat F5).
        if (this.drawer) {
          this._drawerObserver = new MutationObserver(() => {
            if (this.drawer.classList.contains('active')) this.toggle(false);
          });
          this._drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
        }
      }

      disconnectedCallback() {
        if (this.addButton && this._onAdd) this.addButton.removeEventListener('click', this._onAdd);
        if (this._io) this._io.disconnect();
        if (this._unsub) this._unsub();
        if (this._drawerObserver) this._drawerObserver.disconnect();
      }

      toggle(show) {
        if (show && this.drawer && this.drawer.classList.contains('active')) return;
        this.classList.toggle('nf-sticky-atc--visible', !!show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
      }

      onVariantChange(event) {
        const data = event && event.data;
        if (!data) return;
        const variant = data.variant;
        const html = data.html;

        // Preco: copia o bloco recem-renderizado pelo servidor (ja localizado).
        if (this.priceEl && html) {
          const src = html.getElementById('price-' + this.sectionId);
          if (src) this.priceEl.innerHTML = src.innerHTML;
        }

        // Estado do botao espelha a disponibilidade da variante.
        if (this.addButton) {
          const disabled = !variant || variant.available === false;
          this.addButton.toggleAttribute('disabled', disabled);
          if (this.addLabel && window.variantStrings) {
            this.addLabel.textContent = disabled ? window.variantStrings.soldOut : window.variantStrings.addToCart;
          }
        }

        // Imagem da variante.
        if (this.img && this.img.tagName === 'IMG' && variant && variant.featured_media && variant.featured_media.preview_image) {
          const src = variant.featured_media.preview_image.src;
          if (src) this.img.src = this.sized(src, 96);
        }

        this.updateInstallments(variant);
      }

      updateInstallments(variant) {
        if (!this.installments || !variant || typeof variant.price !== 'number') return;
        const el = this.installments;
        const result = window.nfInstallmentsMath.compute(variant.price, {
          max: el.getAttribute('data-nf-installments-max'),
          minCents: el.getAttribute('data-nf-installments-min-cents'),
          pixPct: el.getAttribute('data-nf-pix-pct'),
        });
        if (!result.count) return; // variante sem preco: mantem o que estava

        const currency =
          el.getAttribute('data-nf-installments-currency') ||
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
          'BRL';
        const locale = document.documentElement.lang || 'pt-BR';
        let format;
        try {
          const fmt = new Intl.NumberFormat(locale, { style: 'currency', currency });
          format = (cents) => fmt.format(cents / 100);
        } catch (e) {
          return; /* formatacao indisponivel: mantem o valor anterior */
        }

        const pixPart = el.querySelector('[data-nf-pix-part]');
        const pixValue = el.querySelector('[data-nf-pix-value]');
        const showPix = result.pixCents !== null;
        if (pixValue && showPix) pixValue.textContent = format(result.pixCents);
        if (pixPart) pixPart.classList.toggle('hidden', !showPix);

        const instPart = el.querySelector('[data-nf-installments-part]');
        const countEl = el.querySelector('[data-nf-installments-count]');
        const valueEl = el.querySelector('[data-nf-installments-value]');
        const showInst = result.count >= 2;
        if (countEl) countEl.textContent = String(result.count);
        if (valueEl) valueEl.textContent = format(result.perCents);
        if (instPart) instPart.classList.toggle('hidden', !showInst);

        const sep = el.querySelector('[data-nf-installments-sep]');
        if (sep) sep.classList.toggle('hidden', !(showPix && showInst));
      }

      sized(url, width) {
        if (!url) return url;
        return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
      }
    }
  );
}
